import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiMenu, FiX, FiChevronRight, FiLogOut,
  FiHome, FiShoppingBag, FiInfo, FiMail, FiShoppingCart,
  FiPackage, FiUser, FiShield,
} from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { AuthenticatedOnly, GuestOnly } from './AuthGuard';

const SAGE = '#5C6C4B';

const Nav = styled.nav`
  background: #ffffff;
  padding: 0 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid #f1f3f4;
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(10px);
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Jost Light', sans-serif;
  width: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0;
    width: 100%;
    min-width: 0;
  }
`;

const Logo = styled(Link)`
  color: #1a1a1a;
  text-decoration: none;
  font-size: 1.25rem;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  letter-spacing: -0.025em;
`;

const LogoImage = styled.img`
  height: 92px;
  width: 92px;
  object-fit: cover;
  border-radius: 0;
  box-shadow: none;

  @media (max-width: 768px) {
    height: 64px;
    width: 64px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: none;
  border: none;
  color: #4a5568;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;

  svg { width: 24px; height: 24px; }

  &:hover {
    background: #f7fafc;
    color: #2d3748;
  }

  @media (max-width: 768px) {
    display: inline-flex;
  }
`;

/* ---- mobile drawer ---------------------------------------------------- */
const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(26, 26, 26, 0.45);
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.25s ease, visibility 0.25s ease;
  z-index: 1100;

  @media (min-width: 769px) { display: none; }
`;

const MobileDrawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(86vw, 360px);
  background: #fff;
  box-shadow: ${props => (props.$isOpen ? '-10px 0 44px rgba(0, 0, 0, 0.18)' : 'none')};
  transform: translateX(${props => (props.$isOpen ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1101;
  display: flex;
  flex-direction: column;
  font-family: 'Jost Light', sans-serif;

  @media (min-width: 769px) { display: none; }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid #f1f3f4;
  flex-shrink: 0;
`;

const DrawerGreeting = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 0;

  span {
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #9a9a9a;
  }
  strong {
    font-weight: 500;
    font-size: 1.1rem;
    color: #1a1a1a;
    font-family: 'Jost', sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const DrawerClose = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  background: none;
  border: none;
  color: #4a5568;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;

  svg { width: 22px; height: 22px; }
  &:hover { background: #f7fafc; color: #1a1a1a; }
`;

const DrawerBody = styled.nav`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0.5rem 0;
`;

const DrawerLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.95rem 1.25rem;
  min-height: 52px;
  color: ${props => (props.$active ? SAGE : '#222')};
  background: ${props => (props.$active ? '#f6f8f3' : 'transparent')};
  text-decoration: none;
  font-family: 'Jost Light', sans-serif;
  font-weight: 300;
  font-size: 1.02rem;
  letter-spacing: 0.14em;
  border-bottom: 1px solid #f5f5f5;
  transition: background 0.18s ease, color 0.18s ease;

  .label { display: inline-flex; align-items: center; gap: 0.85rem; min-width: 0; }
  .label svg { width: 19px; height: 19px; color: ${props => (props.$active ? SAGE : '#9a9a8e')}; flex-shrink: 0; }
  .chevron { width: 18px; height: 18px; color: #cfcfc6; flex-shrink: 0; }

  &.admin { color: #3a6ea5; .label svg { color: #3a6ea5; } }

  &:hover, &:active {
    background: #f6f8f3;
    color: ${SAGE};
    .label svg { color: ${SAGE}; }
  }
`;

const CartBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: ${SAGE};
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: 'Jost', sans-serif;
`;

const DrawerSectionLabel = styled.div`
  padding: 1.25rem 1.25rem 0.5rem;
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #a8a89c;
`;

const DrawerFooter = styled.div`
  flex-shrink: 0;
  padding: 1rem 1.25rem calc(1rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #f1f3f4;
`;

const DrawerLogout = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem;
  min-height: 48px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #ecd5d0;
  border-radius: 8px;
  font-family: 'Jost Light', sans-serif;
  font-size: 0.95rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;

  svg { width: 17px; height: 17px; }
  &:hover { background: #fdf3f1; border-color: #c0392b; }
`;

const DrawerAuthRow = styled.div`
  display: flex;
  gap: 0.6rem;
`;

const DrawerAuthLink = styled(Link)`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem;
  min-height: 48px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.92rem;
  letter-spacing: 0.04em;
  transition: all 0.2s ease;

  &.ghost { color: #222; border: 1px solid #e2e2da; background: #fff; }
  &.ghost:hover { border-color: ${SAGE}; color: ${SAGE}; }
  &.primary { color: #fff; background: ${SAGE}; border: 1px solid ${SAGE}; }
  &.primary:hover { background: #4b5a3d; }
`;

/* ---- desktop user menu ------------------------------------------------ */
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #4a5568;
  position: relative;

  @media (max-width: 768px) {
    position: static;
    flex: 1;
    min-width: 0;
    max-width: 30vw;
    justify-content: center;
    background: transparent;
    padding: 0;
    box-shadow: none;
    z-index: auto;
    pointer-events: auto;
    margin-left: -16px;
  }
`;

const UserName = styled.span`
  font-weight: 300;
  font-family: 'Jost Light', sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #f7fafc;
    color: #2d3748;
  }

  @media (max-width: 768px) {
    display: block;
    margin: 0 auto 0 -8px;
    font-size: 0.8rem;
    font-weight: 400;
    background: #fff;
    text-align: center;
    width: 100%;
    max-width: 30vw;
    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
    pointer-events: auto;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  font-family: 'Jost Light', sans-serif;
  min-width: 180px;
  z-index: 1001;
  opacity: ${props => (props.$isOpen ? 1 : 0)};
  visibility: ${props => (props.$isOpen ? 'visible' : 'hidden')};
  transform: ${props => (props.$isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  transition: all 0.2s ease;
  margin-top: 0.5rem;

  /* keep hover-to-open on real desktop pointers; touch uses click state */
  @media (hover: hover) and (pointer: fine) {
    ${UserInfo}:hover & {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    right: 20px;
    width: 12px;
    height: 12px;
    background: white;
    border-left: 1px solid #e2e8f0;
    border-top: 1px solid #e2e8f0;
    transform: rotate(45deg);
  }

  /* desktop-only menu; the mobile drawer replaces it on small screens */
  @media (max-width: 768px) { display: none; }
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  color: #4a5568;
  text-decoration: none;
  font-weight: 300;
  font-family: 'Jost Light', sans-serif;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f7fafc;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f7fafc;
    color: #5C6C4B;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.75rem 1rem;
  color: #4a5568;
  text-decoration: none;
  font-weight: 300;
  font-family: 'Jost Light', sans-serif;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f7fafc;

  &:hover {
    background: #f7fafc;
    color: #c53030;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 0.25rem 0;
`;

const AuthBox = styled.div`
  display: flex;
  align-items: center;
  border: 0.5px solid #000;
  padding: 0.15rem 0.5rem;
  background: #fff;
  gap: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.05rem 0.2rem;
    gap: 0.2rem;
  }
`;

const AuthBoxLink = styled(Link)`
  color: #222;
  text-decoration: none;
  font-weight: 300;
  font-size: 0.8rem;
  font-family: 'Jost Light', sans-serif;
  padding: 0.4rem 0.9rem;
  background: none;
  border: none;
  transition: color 0.2s;
  &:hover {
    color: #5C6C4B;
    text-decoration: none;
  }

  @media (max-width: 768px) {
    font-size: 0.92rem;
    padding: 0.22rem 0.5rem;
    white-space: nowrap;
  }
`;

const AuthDivider = styled.span`
  display: inline-block;
  width: 0.5px;
  height: 16px;
  background: #000;
  margin: 0 0.1rem;
`;

const CenterNavLinks = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: clamp(0.5rem, 2vw, 2.2rem);
  flex-wrap: nowrap;
  min-width: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CenterNavLink = styled(Link)`
  color: #222;
  text-decoration: none;
  font-weight: 300;
  font-size: clamp(0.85rem, 1.5vw, 1.05rem);
  letter-spacing: 0.14em;
  font-family: 'Jost Light', sans-serif;
  padding: 0.3em 0.2em;
  transition: color 0.2s;
  background: none;
  border: none;
  min-width: 0;
  flex-shrink: 1;
  text-align: center;
  white-space: nowrap;

  &:hover {
    color: #5C6C4B;
    text-decoration: none;
  }
`;

const VerticalDivider = styled.div`
  width: 0.5px;
  height: 76px;
  background:rgb(73, 72, 72);
  margin: 0 1.5rem;
  border-radius: 2px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navRef = useRef(null);
  const { getCartItemCount } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const closeMobile = () => setIsMobileMenuOpen(false);
  const cartCount = getCartItemCount();

  // Close menus when clicking/tapping outside the navbar or pressing Escape.
  useEffect(() => {
    if (!isUserMenuOpen && !isMobileMenuOpen) return undefined;

    const handlePointer = (e) => {
      // The mobile drawer is portaled to <body> and closes via its own scrim,
      // so this only manages the desktop user dropdown.
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setIsUserMenuOpen(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isUserMenuOpen, isMobileMenuOpen]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    closeMobile();
    setIsUserMenuOpen(false);
    await logout();
    toast.success('Başarıyla çıkış yaptınız.', { autoClose: 1500 });
    navigate('/');
  };

  const navItems = [
    { to: '/', label: 'Ana Sayfa', icon: FiHome },
    { to: '/products', label: 'Ürünlerimiz', icon: FiShoppingBag },
    { to: '/about', label: 'Hakkımızda', icon: FiInfo },
    { to: '/contact', label: 'İletişim', icon: FiMail },
  ];

  return (
    <Nav ref={navRef}>
      <NavContainer>
        {/* Left: Logo */}
        <Logo to="/">
          <LogoImage src="/images/moria-logo.webp" alt="Moria Zeytinyağı Logo" />
        </Logo>
        <VerticalDivider />
        {/* Center: Nav Links */}
        <CenterNavLinks>
          <CenterNavLink to="/">Ana Sayfa</CenterNavLink>
          <CenterNavLink to="/products">Ürünlerimiz</CenterNavLink>
          <CenterNavLink to="/about">Hakkımızda</CenterNavLink>
          <CenterNavLink to="/contact">İletişim</CenterNavLink>
          <CenterNavLink to="/cart">Sepet</CenterNavLink>
        </CenterNavLinks>
        {/* Right: Auth or User */}
        <AuthenticatedOnly>
          <UserInfo data-cy="user-info">
            <UserName
              onClick={() => { setIsUserMenuOpen(open => !open); setIsMobileMenuOpen(false); }}
              role="button"
              tabIndex={0}
              aria-haspopup="true"
              aria-expanded={isUserMenuOpen}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsUserMenuOpen(open => !open); setIsMobileMenuOpen(false); } }}
            >
              Merhaba, {user?.firstName || ''}
            </UserName>
            <UserDropdown data-cy="user-dropdown" $isOpen={isUserMenuOpen}>
              <DropdownItem to="/order-history" onClick={() => setIsUserMenuOpen(false)}>Siparişlerim</DropdownItem>
              <DropdownItem to="/profile" onClick={() => setIsUserMenuOpen(false)}>Profilim</DropdownItem>
              {user?.isAdmin && (
                <>
                  <DropdownDivider />
                  <DropdownItem to="/admin" onClick={() => setIsUserMenuOpen(false)} style={{ color: '#3498db', fontWeight: 'bold' }}>
                    Admin Portal
                  </DropdownItem>
                </>
              )}
              <DropdownDivider />
              <DropdownButton data-cy="logout-button" onClick={handleLogout}>
                <FiLogOut />
                Çıkış Yap
              </DropdownButton>
            </UserDropdown>
          </UserInfo>
        </AuthenticatedOnly>
        <GuestOnly>
          <AuthBox>
            <AuthBoxLink to="/login">Giriş Yap</AuthBoxLink>
            <AuthDivider />
            <AuthBoxLink to="/register">Kayıt Ol</AuthBoxLink>
          </AuthBox>
        </GuestOnly>
        <MobileMenuButton
          onClick={() => { setIsMobileMenuOpen(open => !open); setIsUserMenuOpen(false); }}
          aria-label="Menüyü aç"
          aria-expanded={isMobileMenuOpen}
        >
          <FiMenu />
        </MobileMenuButton>
      </NavContainer>

      {/* Mobile slide-in drawer — portaled to body to escape the navbar's
          backdrop-filter containing block and stacking context */}
      {createPortal(
        <>
          <MobileOverlay $isOpen={isMobileMenuOpen} onClick={closeMobile} aria-hidden="true" />
          <MobileDrawer $isOpen={isMobileMenuOpen} role="dialog" aria-modal="true" aria-label="Menü">
        <DrawerHeader>
          <DrawerGreeting>
            <AuthenticatedOnly>
              <span>Merhaba</span>
              <strong>{user?.firstName || 'Hoş geldiniz'}</strong>
            </AuthenticatedOnly>
            <GuestOnly>
              <strong>Menü</strong>
            </GuestOnly>
          </DrawerGreeting>
          <DrawerClose onClick={closeMobile} aria-label="Menüyü kapat">
            <FiX />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody>
          {navItems.map(({ to, label, icon: Icon }) => (
            <DrawerLink key={to} to={to} $active={pathname === to} onClick={closeMobile}>
              <span className="label"><Icon />{label}</span>
              <FiChevronRight className="chevron" />
            </DrawerLink>
          ))}
          <DrawerLink to="/cart" $active={pathname === '/cart'} onClick={closeMobile}>
            <span className="label"><FiShoppingCart />Sepet</span>
            {cartCount > 0
              ? <CartBadge>{cartCount}</CartBadge>
              : <FiChevronRight className="chevron" />}
          </DrawerLink>

          <AuthenticatedOnly>
            <DrawerSectionLabel>Hesabım</DrawerSectionLabel>
            <DrawerLink to="/order-history" $active={pathname === '/order-history'} onClick={closeMobile}>
              <span className="label"><FiPackage />Siparişlerim</span>
              <FiChevronRight className="chevron" />
            </DrawerLink>
            <DrawerLink to="/profile" $active={pathname === '/profile'} onClick={closeMobile}>
              <span className="label"><FiUser />Profilim</span>
              <FiChevronRight className="chevron" />
            </DrawerLink>
            {user?.isAdmin && (
              <DrawerLink className="admin" to="/admin" $active={pathname.startsWith('/admin')} onClick={closeMobile}>
                <span className="label"><FiShield />Admin Portal</span>
                <FiChevronRight className="chevron" />
              </DrawerLink>
            )}
          </AuthenticatedOnly>
        </DrawerBody>

        <DrawerFooter>
          <AuthenticatedOnly>
            <DrawerLogout onClick={handleLogout}>
              <FiLogOut /> Çıkış Yap
            </DrawerLogout>
          </AuthenticatedOnly>
          <GuestOnly>
            <DrawerAuthRow>
              <DrawerAuthLink className="ghost" to="/login" onClick={closeMobile}>Giriş Yap</DrawerAuthLink>
              <DrawerAuthLink className="primary" to="/register" onClick={closeMobile}>Kayıt Ol</DrawerAuthLink>
            </DrawerAuthRow>
          </GuestOnly>
        </DrawerFooter>
      </MobileDrawer>
        </>,
        document.body
      )}
    </Nav>
  );
};

export default Navbar;
